import { useEffect, useState } from "react";
import { GridLoader } from "react-spinners";

import { Box, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";

import { runConversationComparison } from "../../data/api/botpressComparisonRunner.js";
import * as BotpressAPI from "../../data/api/omniBotpressAPI.js";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogMessage from "../ui/omniDialogMessage";
import DialogTitle from "../ui/omniDialogTitle";

const PHASE_LABEL = {
  "loading-conversations": "Loading both conversations…",
  "creating-user": "Connecting to the analysis bot…",
  "sending-data": "Sending the conversations for comparison…",
  "creating-conversation": "Starting the analysis…",
  "requesting-analysis": "Requesting the comparison…",
  "awaiting-analysis": "Waiting for the analysis bot's results…",
  done: "Comparison complete.",
};

const loadConversationFrom = async (botODM, conversationId) => {
  if (botODM == null || !conversationId) {
    return null;
  }
  try {
    const messages = await BotpressAPI.loadConversation({ botODM, conversationId });
    return Array.isArray(messages) && messages.length > 0 ? messages : null;
  } catch (error) {
    console.error("Compare: failed to load conversation", error);
    return null;
  }
};

const BotpressCompareConversationsDialog = props => {
  const {
    open,
    onClose,
    onComplete,
    assessmentRow,
    testBotODM,
    botpressBotODM,
    analyticsBotODM,
    maxWidth = "sm",
    fullWidth = true,
  } = props;

  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [phase, setPhase] = useState("loading-conversations");
  const [isRunning, setIsRunning] = useState(true);
  const [results, setResults] = useState(null);
  const [errorText, setErrorText] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        setIsRunning(true);
        setErrorText(null);
        setResults(null);
        setPhase("loading-conversations");

        const newConversation = await loadConversationFrom(
          testBotODM,
          assessmentRow.conversationId,
        );
        let originalConversation = await loadConversationFrom(
          botpressBotODM,
          assessmentRow.originalConversationId,
        );
        if (originalConversation == null) {
          originalConversation = await loadConversationFrom(
            testBotODM,
            assessmentRow.originalConversationId,
          );
        }

        if (newConversation == null || originalConversation == null) {
          throw new Error("Could not load both conversations to compare.");
        }

        const { results: runResults, rawReply } = await runConversationComparison({
          analyticsBotODM,
          originalConversation,
          newConversation,
          onProgress: event => {
            if (!cancelled) {
              setPhase(event.phase);
            }
          },
        });

        if (cancelled) {
          return;
        }
        if (runResults == null) {
          throw new Error(
            rawReply
              ? `The analysis bot didn't return comparison results: "${rawReply}"`
              : "No response from the analysis bot.",
          );
        }
        setResults(runResults);
        setIsRunning(false);
      } catch (error) {
        if (cancelled) {
          return;
        }
        console.error("Compare Conversations failed:", error);
        setErrorText(error.message || "Comparison failed.");
        setIsRunning(false);
        enqueueSnackbar(error.message || "Comparison failed.", { variant: "error" });
      }
    };

    if (open) {
      run();
    }

    return () => {
      cancelled = true;
    };
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSaveHandler = () => {
    if (results != null) {
      onComplete(results);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onClose}>Compare Conversations</DialogTitle>
      <DialogContent>
        {isRunning && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: theme.spacing(2),
              padding: theme.spacing(3),
            }}
          >
            <GridLoader size={16} color={theme.palette.primary.main} />
            <Typography variant="body1" color="textSecondary">
              {PHASE_LABEL[phase] || "Working…"}
            </Typography>
          </Box>
        )}

        {!isRunning && errorText && <DialogMessage>{errorText}</DialogMessage>}

        {!isRunning && !errorText && results && (
          <Box>
            <DialogMessage>Comparison summary</DialogMessage>
            <Typography
              variant="body1"
              sx={{ whiteSpace: "pre-wrap", marginTop: theme.spacing(1) }}
            >
              {results.summary}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="contained" disabled={isRunning}>
          {results ? "Discard" : "Close"}
        </Button>
        {!isRunning && !errorText && results && (
          <Button onClick={onSaveHandler} color="secondary" variant="contained">
            Save Results
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BotpressCompareConversationsDialog;
